/**
 * QZ Printer Configuration Component
 * 
 * Allows users to:
 * 1. Connect to QZ Tray
 * 2. Scan available printers
 * 3. Configure printers for each role (Receipt, Kitchen, Bar)
 * 4. Test print for verification
 * 5. Save configuration
 */

import { useState, useEffect } from 'react';
import { useQZPrint, QZStatus } from '../../contexts/QZPrintContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui/alert';
import { toast } from 'sonner';
import {
  Printer,
  Wifi,
  WifiOff,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Play,
  Settings2,
  Download,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';

export function QZPrinterConfig() {
  const {
    status,
    isConnected,
    error,
    printers,
    printerConfig,
    loading,
    connect,
    disconnect,
    refreshPrinters,
    configurePrinter,
    testPrint,
  } = useQZPrint();

  const [scanning, setScanning] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  // Scan printers
  const handleScanPrinters = async () => {
    setScanning(true);
    try {
      await refreshPrinters();
      toast.success(`Ditemukan ${printers.length} printer`);
    } catch (err) {
      toast.error('Gagal scan printer');
    } finally {
      setScanning(false);
    }
  };

  // Test print for a role
  const handleTestPrint = async (role) => {
    const printerName = printerConfig[role];
    if (!printerName) {
      toast.error(`Pilih printer untuk ${role} terlebih dahulu`);
      return;
    }

    setTesting(prev => ({ ...prev, [role]: true }));
    setTestResults(prev => ({ ...prev, [role]: null }));

    try {
      const result = await testPrint(printerName);
      setTestResults(prev => ({ ...prev, [role]: result }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [role]: { success: false, error: err.message } }));
    } finally {
      setTesting(prev => ({ ...prev, [role]: false }));
    }
  };

  // Handle printer selection change
  const handlePrinterChange = (role, value) => {
    configurePrinter(role, value === 'none' ? null : value);
    setTestResults(prev => ({ ...prev, [role]: null }));
  };

  return (
    <div className="space-y-6" data-testid="qz-printer-config">
      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {isConnected ? (
                  <Wifi className="h-5 w-5 text-green-500" />
                ) : (
                  <WifiOff className="h-5 w-5 text-muted-foreground" />
                )}
                QZ Tray Connection
              </CardTitle>
              <CardDescription>
                Direct printing via QZ Tray local service
              </CardDescription>
            </div>
            <Badge variant={isConnected ? 'default' : 'secondary'}>
              {status === QZStatus.CONNECTED && 'Connected'}
              {status === QZStatus.CONNECTING && 'Connecting...'}
              {status === QZStatus.ERROR && 'Error'}
              {status === QZStatus.DISCONNECTED && 'Disconnected'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Connection Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Not Connected - Show Instructions */}
          {!isConnected && (
            <Alert>
              <Download className="h-4 w-4" />
              <AlertTitle>QZ Tray Diperlukan</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  QZ Tray harus diinstall dan berjalan untuk direct printing.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href="https://qz.io/download/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download QZ Tray
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                  <Button onClick={connect} disabled={loading}>
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Wifi className="h-4 w-4 mr-2" />
                    )}
                    Connect
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Connected - Show Actions */}
          {isConnected && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleScanPrinters}
                disabled={scanning}
              >
                {scanning ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Scan Printers
              </Button>
              <Button variant="outline" onClick={disconnect}>
                <WifiOff className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
              {printers.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {printers.length} printer ditemukan
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Printer Configuration Cards */}
      {isConnected && (
        <>
          {/* Receipt Printer - REQUIRED */}
          <PrinterRoleCard
            role="receipt"
            title="Printer Kasir (Receipt)"
            description="Cetak struk pelanggan - WAJIB dikonfigurasi"
            icon={<Printer className="h-5 w-5 text-green-500" />}
            required
            printers={printers}
            selectedPrinter={printerConfig.receipt}
            onPrinterChange={(value) => handlePrinterChange('receipt', value)}
            onTest={() => handleTestPrint('receipt')}
            testing={testing.receipt}
            testResult={testResults.receipt}
          />

          {/* Kitchen Printer */}
          <PrinterRoleCard
            role="kitchen"
            title="Printer Dapur (Kitchen)"
            description="Cetak tiket pesanan makanan"
            icon={<Printer className="h-5 w-5 text-orange-500" />}
            printers={printers}
            selectedPrinter={printerConfig.kitchen}
            onPrinterChange={(value) => handlePrinterChange('kitchen', value)}
            onTest={() => handleTestPrint('kitchen')}
            testing={testing.kitchen}
            testResult={testResults.kitchen}
          />

          {/* Bar Printer */}
          <PrinterRoleCard
            role="bar"
            title="Printer Bar"
            description="Cetak tiket pesanan minuman"
            icon={<Printer className="h-5 w-5 text-blue-500" />}
            printers={printers}
            selectedPrinter={printerConfig.bar}
            onPrinterChange={(value) => handlePrinterChange('bar', value)}
            onTest={() => handleTestPrint('bar')}
            testing={testing.bar}
            testResult={testResults.bar}
          />
        </>
      )}

      {/* Important Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Catatan Penting</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 text-destructive" />
            <p>
              <strong className="text-foreground">Receipt</strong> hanya dapat
              dicetak via QZ Tray. Browser fallback tidak tersedia untuk struk.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
            <p>
              <strong className="text-foreground">Kitchen/Bar</strong> akan
              menggunakan browser print sebagai fallback jika QZ Tray offline.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Settings2 className="h-4 w-4 mt-0.5" />
            <p>
              Konfigurasi disimpan otomatis. QZ Tray akan auto-reconnect jika
              terputus.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PrinterRoleCard({
  role,
  title,
  description,
  icon,
  required,
  printers,
  selectedPrinter,
  onPrinterChange,
  onTest,
  testing,
  testResult,
}) {
  return (
    <Card data-testid={`printer-config-${role}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <div>
              <CardTitle className="text-base">
                {title}
                {required && (
                  <Badge variant="destructive" className="ml-2 text-xs">
                    WAJIB
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">{description}</CardDescription>
            </div>
          </div>
          {testResult && (
            <Badge variant={testResult.success ? 'default' : 'destructive'}>
              {testResult.success ? (
                <>
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Berhasil
                </>
              ) : (
                <>
                  <XCircle className="h-3 w-3 mr-1" />
                  Gagal
                </>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Pilih Printer</Label>
            <Select
              value={selectedPrinter || 'none'}
              onValueChange={onPrinterChange}
            >
              <SelectTrigger data-testid={`select-printer-${role}`}>
                <SelectValue placeholder="Pilih printer..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">-- Tidak digunakan --</span>
                </SelectItem>
                {printers.map((printer) => (
                  <SelectItem key={printer.name} value={printer.name}>
                    {printer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={onTest}
            disabled={!selectedPrinter || testing}
            data-testid={`test-printer-${role}`}
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
        </div>
        {testResult && !testResult.success && testResult.error && (
          <p className="text-xs text-destructive mt-2">{testResult.error}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default QZPrinterConfig;
